<script lang="ts">
  import Checkbox from '$src/js/modules/ui/components/form/Checkbox.svelte';
  import FormField from '$src/js/modules/ui/components/form/FormField.svelte';
  import { MapboxService } from '$src/js/modules/mapbox/MapboxService';
  import DbrContact from '$src/js/modules/ui/components/user/DbrContact.svelte';
  import { onMount } from 'svelte';
  import { initBouncerValidation } from '$src/js/modules/form-validation/init-bouncer';
  import type { ResponseStatus } from '$src/types/forms';
  import type { AgeGroupsType, UrlType, UserType } from '$src/types/user';
  import Success from '$src/js/modules/request-activity/templates/Success.svelte';
  import jump from 'jump.js';
  import type { ProgramItemTypeAdd } from '$src/types/events';

  const {
    ageGroups = {},
    user = null,
    profileUrl = '',
  }: {
    ageGroups?: AgeGroupsType;
    user?: UserType | null;
    profileUrl?: UrlType;
  } = $props();

  let streetNameLocation = $state('');
  let streetNumberLocation = $state('');
  let streetNameInvoice = $state('');
  let streetNumberInvoice = $state('');
  let streetNameShipping = $state('');
  let streetNumberShipping = $state('');
  let errorMessage = $state('');
  let locationErrorMessage = $state({
    location: '',
    invoiceLocation: '',
    shippingLocation: '',
  });
  let loading = $state(false);
  let submitted = $state(false);

  interface TshirtItemType {
    ageGroup: string;
    tshirtAmount: string;
  }

  interface LocationType {
    name: string;
    street: string;
    zipcode: string;
    city: string;
    country: string;
    lat: number;
    lng: number;
  }

  interface FormDataType {
    name: string;
    activityType: number;
    organization: string;
    organizationWebsite: string;
    location: LocationType;
    province: string;
    program: ProgramItemTypeAdd[];
    dateEvent: string;
    medalAmount: string;
    tshirtAmount: TshirtItemType[];
    helmetAmount: string;
    barrierTapeAmount: string;
    materialDelivery: string;
    invoiceOrganization: string;
    invoiceContactperson: string;
    invoiceContactpersonEmail: string;
    invoiceLocation: LocationType;
    shippingOrganization: string;
    shippingEmail: string;
    shippingLocation: LocationType;
    extraEventInfo: string;
    remarks: string;
  }

  let formData = $state<FormDataType>({
    name: '',
    activityType: window.activityTypeId,
    organization: '',
    organizationWebsite: '',
    location: {
      name: '',
      street: '',
      zipcode: '',
      city: '',
      country: '',
      lat: 0,
      lng: 0,
    },
    province: '-',
    program: [],
    dateEvent: '',
    medalAmount: '',
    tshirtAmount: [],
    helmetAmount: '',
    barrierTapeAmount: '',
    materialDelivery: '',
    invoiceOrganization: '',
    invoiceContactperson: '',
    invoiceContactpersonEmail: '',
    invoiceLocation: {
      name: '',
      street: '',
      zipcode: '',
      city: '',
      country: '',
      lat: 0,
      lng: 0,
    },
    shippingOrganization: '',
    shippingEmail: '',
    shippingLocation: {
      name: '',
      street: '',
      zipcode: '',
      city: '',
      country: '',
      lat: 0,
      lng: 0,
    },
    extraEventInfo: '',
    remarks: '',
  });

  const isAgeGroupSelected = (id: string) =>
    formData.program.some((item) => item.ageGroup === id);

  $effect(() => {
    formData.location.street = `${streetNameLocation} ${streetNumberLocation}`;
    formData.invoiceLocation.street = `${streetNameInvoice} ${streetNumberInvoice}`;
    formData.shippingLocation.street = `${streetNameShipping} ${streetNumberShipping}`;
  });

  const addProgramItem = (ageGroup: string) => {
    const existingIndex = formData.program.findIndex(
      (item) => item.ageGroup === ageGroup
    );
    if (existingIndex === -1) {
      formData.program = [...formData.program, { ageGroup, timeStart: '' }];
    }
  };

  const removeProgramItem = (ageGroup: string) => {
    formData.program = formData.program.filter(
      (item) => item.ageGroup !== ageGroup
    );
  };

  const updateProgramTime = (ageGroup: string, timeStart: string) => {
    formData.program = formData.program.map((item) =>
      item.ageGroup === ageGroup ? { ...item, timeStart } : item
    );
  };

  const addTshirtAmountItem = (ageGroup: string) => {
    const existingIndex = formData.tshirtAmount.findIndex(
      (item) => item.ageGroup === ageGroup
    );
    if (existingIndex === -1) {
      formData.tshirtAmount = [
        ...formData.tshirtAmount,
        { ageGroup, tshirtAmount: '' },
      ];
    }
  };

  const removeTshirtAmountItem = (ageGroup: string) => {
    formData.tshirtAmount = formData.tshirtAmount.filter(
      (item) => item.ageGroup !== ageGroup
    );
  };

  const updateShirtAmount = (ageGroup: string, tshirtAmount: string) => {
    formData.tshirtAmount = formData.tshirtAmount.map((item) =>
      item.ageGroup === ageGroup ? { ...item, tshirtAmount } : item
    );
  };

  const handleProgramCheckboxChange = (id: string, checked: boolean) => {
    if (checked) {
      addProgramItem(id);
      addTshirtAmountItem(id);
    } else {
      removeProgramItem(id);
      removeTshirtAmountItem(id);
    }
  };

  const getCoordinates = async (
    type: 'location' | 'invoiceLocation' | 'shippingLocation'
  ) => {
    if (
      !formData[type].street ||
      !formData[type].zipcode ||
      !formData[type].city ||
      !formData[type].country
    ) {
      return;
    }

    const mapboxService = new MapboxService();
    const geoDataResponse = await mapboxService.getGeoLocationFromString(
      `${formData[type].street} ${formData[type].zipcode} ${formData[type].city} ${formData[type].country}`
    );

    if (
      typeof geoDataResponse.lng !== 'number' ||
      typeof geoDataResponse.lat !== 'number'
    ) {
      locationErrorMessage[type] =
        'Sorry, er is iets fout gegaan bij het ophalen van de locatie. Probeer het met andere gegevens.';
      return;
    }

    locationErrorMessage[type] = '';
    formData[type].lat = geoDataResponse.lat;
    formData[type].lng = geoDataResponse.lng;

    if (type === 'location') {
      formData.province = geoDataResponse.province ?? '-';
    }
  };

  const handleFormSubmit = async (event: Event) => {
    const form = event.target as HTMLFormElement;

    if (
      !(form instanceof HTMLFormElement) ||
      form.dataset.validateForm !== 'rdrEvent'
    ) {
      return;
    }

    event.preventDefault();
    loading = true;

    const formDataObject = new FormData();
    formDataObject.append('name', formData.name);
    formDataObject.append('activityType', formData.activityType.toString());
    formDataObject.append('organization', formData.organization);
    formDataObject.append('organizationWebsite', formData.organizationWebsite);
    formDataObject.append('location', JSON.stringify(formData.location));
    formDataObject.append('province', formData.province);
    formDataObject.append('program', JSON.stringify(formData.program));
    formDataObject.append('dateEvent', formData.dateEvent);
    formDataObject.append('extraEventInfo', formData.extraEventInfo);
    formDataObject.append('remarks', formData.remarks);
    formDataObject.append('medalAmount', formData.medalAmount);
    formDataObject.append(
      'tshirtAmount',
      JSON.stringify(formData.tshirtAmount)
    );
    formDataObject.append('helmetAmount', formData.helmetAmount);
    formDataObject.append('barrierTapeAmount', formData.barrierTapeAmount);
    formDataObject.append('materialDelivery', formData.materialDelivery);
    formDataObject.append('invoiceOrganization', formData.invoiceOrganization);
    formDataObject.append(
      'invoiceContactperson',
      formData.invoiceContactperson
    );
    formDataObject.append(
      'invoiceContactpersonEmail',
      formData.invoiceContactpersonEmail
    );
    formDataObject.append(
      'invoiceLocation',
      JSON.stringify(formData.invoiceLocation)
    );
    formDataObject.append(
      'shippingOrganization',
      formData.shippingOrganization
    );
    formDataObject.append('shippingEmail', formData.shippingEmail);
    formDataObject.append(
      'shippingLocation',
      JSON.stringify(formData.shippingLocation)
    );

    try {
      await new Promise((resolve) => setTimeout(resolve, 400));
      const response = await fetch('/api/activities/dbr/organize/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': window.csrfTokenValue,
        },
        body: JSON.stringify(Object.fromEntries(formDataObject.entries())),
      });

      const responseData = (await response.json()) as {
        status: ResponseStatus;
      };

      if (!response.ok || responseData.status !== 'success') {
        errorMessage =
          'Sorry, er is iets fout gegaan bij het indienen van het evenement. Probeer het opnieuw.';
        loading = false;
        return;
      }

      jump('body');

      submitted = true;
      loading = false;
    } catch {
      errorMessage =
        'Sorry, er is iets fout gegaan bij het indienen van het evenement. Probeer het opnieuw.';
      loading = false;
    }
  };

  onMount(() => {
    initBouncerValidation();

    document.addEventListener('bouncerFormValid', (event) => {
      if (!formData.dateEvent) {
        errorMessage = 'Datum is verplicht.';
        return;
      }

      void handleFormSubmit(event);
    });
  });
</script>

{#if submitted}
  <Success />
{:else}
  <form
    class={`2xl:fluid-gap-[64] flex flex-col gap-12 lg:gap-16 ${loading ? 'loading-box loading-box--bottom' : ''}`}
    data-validate-form="rdrEvent"
  >
    <div class="2xl:fluid-gap-[32] flex flex-col gap-8">
      <h3 class="heading-s">Algemeen</h3>

      <FormField
        background="black"
        type="text"
        label="Naam van de Dikke Banden Race"
        id="name"
        name="name"
        placeholder="Bijv. Dikke Banden Race 's-Hertogenbosch"
        footnote="Dikke Banden Race + plaatsnaam"
        required={true}
        bind:value={formData.name}
      />

      <FormField
        background="black"
        type="text"
        label="Naam organisatie"
        id="organization"
        name="organization"
        required={true}
        bind:value={formData.organization}
      />

      <FormField
        background="black"
        type="text"
        label="Website organisatie"
        id="organizationWebsite"
        name="organizationWebsite"
        required={false}
        customValidation="isURL"
        bind:value={formData.organizationWebsite}
      />
    </div>
    <div class="2xl:fluid-gap-[32] flex flex-col gap-8">
      <h3 class="heading-s">Contactpersoon</h3>

      {#if user && profileUrl}
        <DbrContact {user} {profileUrl} />
      {/if}
    </div>

    <div class="2xl:fluid-gap-[32] flex flex-col gap-8">
      <h3 class="heading-s">Programma</h3>

      <div class="flex flex-col gap-4">
        {#each Object.entries(ageGroups) as [id, label]}
          <Checkbox
            {id}
            name={id}
            theme="green"
            borderColor="white-600"
            checked={isAgeGroupSelected(id)}
            onChange={(e: Event) => {
              const target = e.target as HTMLInputElement;
              handleProgramCheckboxChange(id, target.checked);
            }}
            {label}
          />
          {#if isAgeGroupSelected(id)}
            <FormField
              background="black"
              type="text"
              label="Starttijd"
              id={`time_${id}`}
              name={`time_${id}`}
              required={true}
              value={formData.program.find((item) => item.ageGroup === id)
                ?.timeStart ?? ''}
              attributes={{
                type: 'time',
              }}
              onChange={(e: Event) => {
                const target = e.target as HTMLInputElement;
                updateProgramTime(id, target.value);
              }}
            />
          {/if}
        {/each}
      </div>
    </div>

    <div class="2xl:fluid-gap-[32] flex flex-col gap-8">
      <h3 class="heading-s">Datum & Locatie</h3>

      <FormField
        background="black"
        type="singleDate"
        id="dateEvent"
        name="dateEvent"
        label="Datum"
        placeholder="Selecteer een datum"
        required={true}
        attributes={{
          dateObjects: formData.dateEvent,
        }}
        bind:value={formData.dateEvent}
      />

      <FormField
        background="black"
        type="text"
        label="Locatienaam"
        id="locationName"
        name="locationName"
        required={true}
        placeholder="Bijv. Parcours wielervereniging Utrecht"
        bind:value={formData.location.name}
      />

      <div class="2xl:fluid-gap-[16] flex w-full gap-4">
        <FormField
          background="black"
          type="text"
          label="Straatnaam"
          id="locationStreet"
          name="locationStreet"
          required={true}
          bind:value={streetNameLocation}
          onBlur={() => getCoordinates('location')}
        />
        <FormField
          background="black"
          type="text"
          label="Huisnummer"
          id="locationHouseNumber"
          name="locationHouseNumber"
          required={true}
          bind:value={streetNumberLocation}
          onBlur={() => getCoordinates('location')}
        />
      </div>

      <div class="2xl:fluid-gap-[16] flex w-full gap-4">
        <FormField
          background="black"
          type="text"
          label="Postcode"
          id="locationzipcode"
          name="locationzipcode"
          required={true}
          bind:value={formData.location.zipcode}
          onBlur={() => getCoordinates('location')}
        />
        <FormField
          background="black"
          type="text"
          label="Plaatsnaam"
          id="locationCity"
          name="locationCity"
          required={true}
          bind:value={formData.location.city}
          onBlur={() => getCoordinates('location')}
        />
      </div>

      <FormField
        background="black"
        type="select"
        label="Land"
        id="locationCountry"
        name="locationCountry"
        options={Object.entries(window.countries).map(([key, value]) => ({
          value: key,
          label: value,
        }))}
        required={true}
        bind:value={formData.location.country}
        onChange={() => getCoordinates('location')}
      />

      {#if locationErrorMessage.location}
        <span class="text-16px text-error">{locationErrorMessage.location}</span
        >
      {/if}
    </div>
    <div class="2xl:fluid-gap-[32] flex flex-col gap-8">
      <h3 class="heading-s">Materiaal</h3>

      <span
        class="2xl:fluid-text-[13] flex gap-1 text-[0.8125rem] leading-[120%] font-medium text-[var(--text-color)]"
      >
        Hieronder kun je aangeven welke materialen jullie van ons willen
        ontvangen.
      </span>

      <FormField
        background="black"
        type="number"
        min="0"
        label="Aantal medailles (voor iedere deelnemer)"
        id="medalAmount"
        name="medalAmount"
        required={true}
        bind:value={formData.medalAmount}
      />

      {#each Object.entries(ageGroups) as [id, label]}
        {#if isAgeGroupSelected(id)}
          <FormField
            background="black"
            type="number"
            min="0"
            label={`Aantal t-shirts (${label})`}
            id={`tshirtAmount_${id}`}
            name={`tshirtAmount_${id}`}
            required={true}
            value={formData.tshirtAmount.find((item) => item.ageGroup === id)
              ?.tshirtAmount ?? ''}
            onChange={(e: Event) => {
              const target = e.target as HTMLInputElement;
              updateShirtAmount(id, target.value);
            }}
          />
        {/if}
      {/each}

      <FormField
        background="black"
        type="select"
        label="Aantal helmen"
        options={Object.entries(window.helmets).map(([value, label]) => ({
          value,
          label,
        }))}
        id="helmetAmount"
        name="helmetAmount"
        required={true}
        bind:value={formData.helmetAmount}
      />

      <FormField
        background="black"
        type="select"
        label="Aantal keer afzetlint (500 meter)"
        options={Object.entries(window.barrierTapeOptions).map(
          ([value, label]) => ({
            value,
            label,
          })
        )}
        id="barrierTapeAmount"
        name="barrierTapeAmount"
        required={true}
        bind:value={formData.barrierTapeAmount}
      />

      <FormField
        background="black"
        type="select"
        label="Verzenden of ophalen?"
        options={Object.entries(window.deliveryOptions).map(
          ([value, label]) => ({
            value,
            label,
          })
        )}
        id="materialDelivery"
        name="materialDelivery"
        required={true}
        bind:value={formData.materialDelivery}
      />

      {#if formData.materialDelivery === 'ship'}
        <div class="2xl:fluid-gap-[32] flex flex-col gap-8">
          <h3 class="heading-s">Factuurinformatie</h3>

          <FormField
            background="black"
            type="text"
            label="Naam organisatie"
            id="invoiceOrganization"
            name="invoiceOrganization"
            required={true}
            bind:value={formData.invoiceOrganization}
          />

          <FormField
            background="black"
            type="text"
            label="Naam contactpersoon"
            id="invoiceContactperson"
            name="invoiceContactperson"
            required={true}
            bind:value={formData.invoiceContactperson}
          />

          <FormField
            background="black"
            type="email"
            label="E-mailadres contactpersoon"
            id="invoiceContactpersonEmail"
            name="invoiceContactpersonEmail"
            required={true}
            bind:value={formData.invoiceContactpersonEmail}
          />

          <div class="2xl:fluid-gap-[16] flex w-full gap-4">
            <FormField
              background="black"
              type="text"
              label="Straatnaam"
              id="invoiceStreetName"
              name="invoiceStreetName"
              required={true}
              bind:value={streetNameInvoice}
              onBlur={() => getCoordinates('invoiceLocation')}
            />
            <FormField
              background="black"
              type="text"
              label="Huisnummer"
              id="invoiceStreetNumber"
              name="invoiceStreetNumber"
              required={true}
              bind:value={streetNumberInvoice}
              onBlur={() => getCoordinates('invoiceLocation')}
            />
          </div>

          <div class="2xl:fluid-gap-[16] flex w-full gap-4">
            <FormField
              background="black"
              type="text"
              label="Postcode"
              id="invoiceLocationZipcode"
              name="invoiceLocationZipcode"
              required={true}
              bind:value={formData.invoiceLocation.zipcode}
              onBlur={() => getCoordinates('invoiceLocation')}
            />
            <FormField
              background="black"
              type="text"
              label="Plaatsnaam"
              id="InvoiceLocationCity"
              name="InvoiceLocationCity"
              required={true}
              bind:value={formData.invoiceLocation.city}
              onBlur={() => getCoordinates('invoiceLocation')}
            />
          </div>

          <FormField
            background="black"
            type="select"
            label="Land"
            id="invoiceLocationCountry"
            name="invoiceLocationCountry"
            options={Object.entries(window.countries).map(([key, value]) => ({
              value: key,
              label: value,
            }))}
            required={true}
            bind:value={formData.invoiceLocation.country}
            onChange={() => getCoordinates('invoiceLocation')}
          />

          {#if locationErrorMessage.invoiceLocation}
            <span class="text-16px text-error"
              >{locationErrorMessage.invoiceLocation}</span
            >
          {/if}
        </div>

        <div class="2xl:fluid-gap-[32] flex flex-col gap-8">
          <h3 class="heading-s">Verzendgegevens</h3>

          <FormField
            background="black"
            type="text"
            label="Naam organisatie"
            id="shippingOrganization"
            name="shippingOrganization"
            required={true}
            bind:value={formData.shippingOrganization}
          />

          <FormField
            background="black"
            type="email"
            label="E-mailadres ontvanger"
            id="shippingEmail"
            name="shippingEmail"
            required={true}
            bind:value={formData.shippingEmail}
          />

          <div class="2xl:fluid-gap-[16] flex w-full gap-4">
            <FormField
              background="black"
              type="text"
              label="Straatnaam"
              id="shippingStreetName"
              name="shippingStreetName"
              required={true}
              bind:value={streetNameShipping}
              onBlur={() => getCoordinates('shippingLocation')}
            />
            <FormField
              background="black"
              type="text"
              label="Huisnummer"
              id="shippingStreetNumber"
              name="shippingStreetNumber"
              required={true}
              bind:value={streetNumberShipping}
              onBlur={() => getCoordinates('shippingLocation')}
            />
          </div>

          <div class="2xl:fluid-gap-[16] flex w-full gap-4">
            <FormField
              background="black"
              type="text"
              label="Postcode"
              id="shippingLocationZipcode"
              name="shippingLocationZipcode"
              required={true}
              bind:value={formData.shippingLocation.zipcode}
              onBlur={() => getCoordinates('shippingLocation')}
            />

            <FormField
              background="black"
              type="text"
              label="Plaatsnaam"
              id="shippingLocationCity"
              name="shippingLocationCity"
              required={true}
              bind:value={formData.shippingLocation.city}
              onBlur={() => getCoordinates('shippingLocation')}
            />
          </div>

          <FormField
            background="black"
            type="select"
            label="Land"
            id="shippingLocationCountry"
            name="shippingLocationCountry"
            options={Object.entries(window.countries).map(([key, value]) => ({
              value: key,
              label: value,
            }))}
            required={true}
            bind:value={formData.shippingLocation.country}
            onChange={() => getCoordinates('shippingLocation')}
          />

          {#if locationErrorMessage.shippingLocation}
            <span class="text-16px text-error"
              >{locationErrorMessage.shippingLocation}</span
            >
          {/if}
        </div>
      {/if}
    </div>
    <div class="2xl:fluid-gap-[32] flex flex-col gap-8">
      <h3 class="heading-s">Aanvullend</h3>

      <FormField
        label="Extra informatie over het evenement"
        type="textarea"
        background="black"
        id="extraEventInfo"
        name="extraEventInfo"
        bind:value={formData.extraEventInfo}
      />

      <FormField
        label="Heb je nog aanvullende vragen/opmerkingen voor het Ready2Race team?"
        type="textarea"
        background="black"
        id="remarks"
        name="remarks"
        bind:value={formData.remarks}
      />

      <Checkbox
        theme="yellow"
        borderColor="white-600"
        label={`Ja, ik ga akkoord met de <a href="${window.termsUrl}" class="underline link-decoration-green underline-offset-[20%]">organisatorvoorwaarden</a> *`}
        id="terms"
        name="terms"
        required={true}
      />
    </div>

    {#if errorMessage}
      <div
        class="text-14px 2xl:fluid-p-[16] border-error border-1 p-4 leading-[180%] font-medium"
      >
        {errorMessage}
      </div>
    {/if}

    <button class="btn-primary-large" type="submit">
      Evenement indienen
    </button>
  </form>
{/if}
